import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { HeroBanner } from "../../components/banner/HeroBanner";
import bgLeftImage from "./../../assets/images/bg-left.svg";
import bgRightImage from "./../../assets/images/bg-right.svg";
import "./AdminDashboard.scss";
import "./ListingPage.scss";
import { TILES, SUB_TILES_MAP, SubTileConfig } from "./tileConfig";
import LoadingWBackdrop from "../../components/common/LoadingWBackdrop";
import { NoResultsFound } from "../../components/common/NoResultsFound";
import {
    LoadStatus,
    ENGAGE_EMPTY_STATE,
    ENGAGE_UNAUTHORIZED_STATE,
    ENGAGE_FAILED_STATE,
    UNAUTHORIZED_REDIRECT_DELAY_MS,
    getLoadStatusFromError,
} from "../../features/engage/engageService";
import { forceLogoutToLogin } from "../../utils/auth/forceLogout";

export const ListingPage = () => {
    const location = useLocation();
    const history = useHistory();
    const [subTiles, setSubTiles] = useState<SubTileConfig[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('ok');

    const tileMatch = TILES.find(t => t.route === location.pathname);
    const bannerTitle = tileMatch?.name || '';
    const bannerDesc = tileMatch?.subtitle || '';
    const bannerLogo = tileMatch?.imgsrcListingPage;
    const loader = tileMatch?.route ? SUB_TILES_MAP[tileMatch.route] : undefined;

    useEffect(() => {
        if (!loader) {
            setSubTiles([]);
            setIsLoading(false);
            setLoadStatus('ok');
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setLoadStatus('ok');
        loader()
            .then((tiles) => {
                if (cancelled) return;
                setSubTiles(tiles);
                setLoadStatus('ok');
            })
            .catch((error) => {
                if (cancelled) return;
                setSubTiles([]);
                setLoadStatus(getLoadStatusFromError(error));
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, [loader]);

    useEffect(() => {
        if (loadStatus !== 'unauthorized') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            forceLogoutToLogin();
        }, UNAUTHORIZED_REDIRECT_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [loadStatus]);

    const handleBackClick = () => {
        history.push("/");
    };

    const handleTileClick = (route: string) => {
        const newWindow = window.open(route, "_blank", "noopener,noreferrer");
        if (newWindow) newWindow.opener = null;
    };

    const handleTileKeyDown = (e: React.KeyboardEvent, route: string) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTileClick(route);
        }
    };

    if (isLoading) {
        return <LoadingWBackdrop />;
    }

    return (
        <div className="app-dashboard-container">
            <HeroBanner
                title={`Welcome To ${bannerTitle}`}
                desc={bannerDesc}
                mainLogoSrc={bannerLogo}
                showBackButton={true}
                onBackClick={handleBackClick}
            />
            <div className="list-tile-container">
                {subTiles.length > 0 ? (
                    subTiles.map((tile: SubTileConfig) => (
                        <div
                            key={tile.id}
                            className="list-tile-item"
                            role="article"
                            aria-labelledby={`tile-title-${tile.id}`}
                            aria-describedby={`tile-desc-${tile.id}`}
                        >
                            {tile.iconSrc && (
                                <div
                                    className="list-tile-icon"
                                    aria-hidden={tile.iconAlt ? undefined : true}
                                >
                                    <img src={tile.iconSrc} alt={tile.iconAlt || ''} />
                                </div>
                            )}
                            <div className="list-tile-content">
                                <h2 id={`tile-title-${tile.id}`} className="list-tile-title">
                                    {tile.title}
                                </h2>
                                <p id={`tile-desc-${tile.id}`} className="list-tile-desc">{tile.desc}</p>
                            </div>
                            {tile.route && (
                                <button
                                    className="list-tile-button"
                                    onClick={() => handleTileClick(tile.route!)}
                                    onKeyDown={(e) => handleTileKeyDown(e, tile.route!)}
                                    aria-label={`Get started with ${tile.title}`}
                                    type="button"
                                >
                                    GET STARTED
                                </button>
                            )}
                        </div>
                    ))
                ) : (
                    <NoResultsFound
                        className="list-tile-empty-state"
                        message={
                            loadStatus === 'unauthorized' ? ENGAGE_UNAUTHORIZED_STATE.message :
                                loadStatus === 'failed' ? ENGAGE_FAILED_STATE.message :
                                    ENGAGE_EMPTY_STATE.message
                        }
                        description={
                            loadStatus === 'unauthorized' ? ENGAGE_UNAUTHORIZED_STATE.description :
                                loadStatus === 'failed' ? ENGAGE_FAILED_STATE.description :
                                    ENGAGE_EMPTY_STATE.description
                        }
                    />
                )}
            </div>
            <div className="dashboard-background-images">
                <img src={bgLeftImage} alt="Background Left" className="bg-left" />
                <img src={bgRightImage} alt="Background Right" className="bg-right" />
            </div>
        </div>
    );
}
